// src/components/SectionConfig.jsx
import React, { useState } from 'react';
import {
    Paper,
    TextField,
    Box,
    Typography,
    Button,
    Grid,
    Divider,
    Chip,
    alpha,
    IconButton,
    Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FieldConfig from './FieldConfig';
import { createNewField } from '../types/schema';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// src/components/FieldListWrapper.jsx
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import ConditionalLogicConfig from './ConditionalLogicConfig';
import SkipNextIcon from '@mui/icons-material/SkipNext';

const FieldListWrapper = ({
    fields,
    onFieldsChange,
    onAddField,
    availableFieldsInCurrentSection = [],
    currentSectionId = ''
}) => {

    // Configure sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px of movement before dragging starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = fields.findIndex(field =>
                field.id === active.id ||
                `field-${fields.findIndex(f => f === active.data.current?.field)}` === active.id
            );
            const newIndex = fields.findIndex(field =>
                field.id === over.id ||
                `field-${fields.findIndex(f => f === over.data.current?.field)}` === over.id
            );

            if (oldIndex !== -1 && newIndex !== -1) {
                const newFields = arrayMove(fields, oldIndex, newIndex);

                // Update all indices
                const updatedFields = newFields.map((field, index) => ({
                    ...field,
                    id: field.id || `field-${index}` // Ensure each field has an ID
                }));

                onFieldsChange(updatedFields);
            }
        }
    };

    // Update a single field
    const handleFieldChange = (index, updatedField) => {
        const newFields = [...fields];
        newFields[index] = { ...updatedField, id: updatedField.id || `field-${index}` };
        onFieldsChange(newFields);
    };

    // Delete a field
    const handleDeleteField = (index) => {
        const newFields = fields.filter((_, i) => i !== index);
        onFieldsChange(newFields);
    };

    // Generate IDs for fields that don't have them
    const fieldsWithIds = fields.map((field, index) => ({
        ...field,
        id: field.id || `field-${index}`
    }));

    return (
        <Box>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={fieldsWithIds.map(f => f.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <Box sx={{ minHeight: 100 }}>
                        {fieldsWithIds.map((field, index) => (
                            <FieldConfig
                                key={field.id}
                                field={field}
                                index={index}
                                onChange={(updatedField) => handleFieldChange(index, updatedField)}
                                onDelete={() => handleDeleteField(index)}
                                totalFields={fields.length}
                                availableFieldsInCurrentSection={availableFieldsInCurrentSection}
                                currentSectionId={currentSectionId}
                            />
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>

            {/* Add Field Button */}
            <Paper
                elevation={0}
                sx={{
                    mt: 2,
                    p: 3,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textAlign: 'center',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                }}
            >
                <Button
                    startIcon={<AddIcon />}
                    onClick={onAddField}
                    variant="outlined"
                    color="primary"
                    size="large"
                    sx={{
                        py: 1.5,
                        px: 3,
                        fontSize: '1rem'
                    }}
                >
                    Add New Field
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Drag and drop fields to reorder them
                </Typography>
            </Paper>
        </Box>
    );
};

const SectionConfig = ({ section, onChange, disabled, allSections, availableFieldsInPreviousSections }) => {

    const [skipLogicExpanded, setSkipLogicExpanded] = useState(false);

    const getAvailableFieldsInCurrentSection = () => {
        return section.fields
            .filter(field => field.name) // Only include fields with names
            .map(field => ({
                sectionId: section.id,
                sectionTitle: section.title,
                fieldName: field.name,
                fieldLabel: field.label || field.name,
                fieldType: field.type
            }));
    };

    const handleSectionChange = (field) => (event) => {
        onChange({
            ...section,
            [field]: event.target.value
        });
    };

    const handleAddField = () => {
        const newField = createNewField();
        onChange({
            ...section,
            fields: [...section.fields, newField]
        });
    };

    const handleFieldChange = (index, updatedField) => {
        const newFields = [...section.fields];
        newFields[index] = updatedField;
        onChange({
            ...section,
            fields: newFields
        });
    };

    const handleDeleteField = (index) => {
        const newFields = section.fields.filter((_, i) => i !== index);
        onChange({
            ...section,
            fields: newFields
        });
    };

    const handleFieldReorder = (fromIndex, toIndex) => {
        const newFields = [...section.fields];
        const [movedField] = newFields.splice(fromIndex, 1);
        newFields.splice(toIndex, 0, movedField);
        onChange({
            ...section,
            fields: newFields
        });
    };

    return (
        <Box sx={{ mt: 4 }}>
            {section.order > 0 && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'secondary.main',
                            transition: 'all 0.3s',
                            '&:hover': {
                                borderColor: 'secondary.dark',
                                boxShadow: 6
                            }
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2}
                            pb={1}
                            borderBottom={2}
                            borderColor="secondary.main"
                        >
                            <SkipNextIcon color="secondary" />
                            <Typography variant="h6" fontWeight={600}>
                                Section Skip Logic
                            </Typography>
                            <Chip
                                label="Optional"
                                size="small"
                                color="secondary"
                                variant="outlined"
                            />
                        </Box>

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ cursor: 'pointer', mb: 2 }}
                            onClick={() => setSkipLogicExpanded(!skipLogicExpanded)}
                        >
                            <Typography variant="subtitle1" fontWeight={600}>
                                Configure when this section should be skipped
                            </Typography>
                            <IconButton size="small">
                                {skipLogicExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>

                        <Collapse in={skipLogicExpanded}>
                            <ConditionalLogicConfig
                                type="section"
                                conditions={section.skipRules || []}
                                onChange={(skipRules) => onChange({ ...section, skipRules })}
                                availableFields={availableFieldsInPreviousSections} // Pass this prop
                                currentSectionId={section.id}
                                allSections={allSections} // Pass all sections for reference
                            />
                        </Collapse>
                    </Paper>
                </>
            )}
            {/* Section Header */}
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    transition: 'all 0.3s',
                    '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 6
                    }
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                    pb={1}
                    borderBottom={2}
                    borderColor="primary.main"
                >
                    <ViewListIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        Section Configuration
                    </Typography>
                    <Chip
                        label={`${section.fields.length} Fields`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>

                <Grid container spacing={3}>
                    <Grid>
                        <TextField
                            fullWidth
                            size="small"
                            label="Section Title"
                            value={section.title}
                            onChange={handleSectionChange('title')}
                            required
                            error={!section.title}
                            helperText="Displayed as section heading"
                        />
                    </Grid>

                    <Grid>
                        <TextField
                            fullWidth
                            size="small"
                            label="Description"
                            value={section.description}
                            onChange={handleSectionChange('description')}
                            multiline
                            rows={2}
                            placeholder="Optional description shown below the section title"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Fields Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" fontWeight={600}>
                        Fields
                    </Typography>
                    <Chip
                        label={section.fields.length}
                        size="small"
                        color="secondary"
                        variant="outlined"
                    />
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddField}
                    sx={{
                        borderStyle: 'dashed',
                        '&:hover': {
                            borderStyle: 'solid'
                        }
                    }}
                >
                    Add Field
                </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box>
                <FieldListWrapper
                    fields={section.fields}
                    onFieldsChange={(newFields) => onChange({ ...section, fields: newFields })}
                    onAddField={handleAddField}
                    availableFieldsInCurrentSection={getAvailableFieldsInCurrentSection()}
                    currentSectionId={section.id}
                />
            </Box>

            {/* Fields List */}
            {/* {section.fields.map((field, index) => (
                <FieldConfig
                    key={field.id}
                    field={field}
                    index={index}
                    onChange={(updatedField) => handleFieldChange(index, updatedField)}
                    onDelete={() => handleDeleteField(index)}
                    onReorder={handleFieldReorder}
                    totalFields={section.fields.length}
                />
            ))} */}

            {/* Empty State */}
            {section.fields.length === 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: alpha('#1976d2', 0.04),
                        borderRadius: 2
                    }}
                >
                    <InfoOutlinedIcon color="action" sx={{ mb: 1 }} />
                    <Typography color="text.secondary">
                        No fields added yet. Click <strong>“Add Field”</strong> to start building this section.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default SectionConfig;
